import React, { useState, useEffect, memo, useMemo, useCallback } from 'react';
import { useHoneyFarm } from '../../context/HoneyFarmContext';
import LevelingBee from './LevelingBee';
import combCommon from '../../assets/Data/honeycombs/1.gif';
import combUncommon from '../../assets/Data/honeycombs/2.gif';
import combRare from '../../assets/Data/honeycombs/3.gif';
import combEpic from '../../assets/Data/honeycombs/4.gif';
import combLegendary from '../../assets/Data/honeycombs/5.gif';

// Level filter options
type LevelFilter = 'all' | number;

interface IBeeLevelerProps {
  hiveIds: number[];
  txPending: boolean;
  setTxPending: (pending: boolean) => void;
  isDesktop: boolean;
  addressBeeTokenIds: number[];
  address: string | undefined | null;
}

// Import honeycomb images (bundled via Webpack)
const combImages = [
  combCommon,     // Common
  combUncommon,   // Uncommon
  combRare,       // Rare
  combEpic,       // Epic
  combLegendary,  // Legendary
];

const combIds = [0, 1, 2, 3, 4];

const BeeLeveler: React.FC<IBeeLevelerProps> = (props) => {
  const { contracts, getBeeLevel } = useHoneyFarm();
  const [combBalance, setCombBalance] = useState<number[]>([0, 0, 0, 0, 0]);
  const [selectedBee, setSelectedBee] = useState<number>(0);
  const [beeOptions, setBeeOptions] = useState<any[]>([]);
  const [selectedCombs, setSelectedCombs] = useState<number[]>([0, 0, 0, 0, 0]);
  const [searchId, setSearchId] = useState<string>('');
  const [levelFilter, setLevelFilter] = useState<LevelFilter>('all');
  const [beeLevels, setBeeLevels] = useState<Record<number, number>>({});
  const [levelsLoading, setLevelsLoading] = useState<boolean>(false);

  // Setup bee options for dropdown
  useEffect(() => {
    const options = props.addressBeeTokenIds.map((id) => ({
      text: `Bees Deluxe #${id}`,
      key: id,
      value: id,
      image: `https://bearsdeluxe.mypinata.cloud/ipfs/QmbpKE2zBKhbTK1CRWWhwDTjo2y7XgaeeFigGpRk717QwL/${id}.png`
    }));
    setBeeOptions(options);
    if (options.every((option) => option.value !== selectedBee)) {
      setSelectedBee(0);
    }
  }, [props.addressBeeTokenIds, selectedBee]);

  // Load bee levels for filtering
  const loadBeeLevels = useCallback(async () => {
    if (!getBeeLevel || props.addressBeeTokenIds.length === 0) return;
    
    setLevelsLoading(true);
    try {
      const levels: Record<number, number> = {};
      // Load levels in batches to avoid too many concurrent requests
      const batchSize = 10;
      for (let i = 0; i < props.addressBeeTokenIds.length; i += batchSize) {
        const batch = props.addressBeeTokenIds.slice(i, i + batchSize);
        const levelPromises = batch.map(async (id) => {
          try {
            const level = await getBeeLevel(id);
            return { id, level };
          } catch {
            return { id, level: 0 };
          }
        });
        const results = await Promise.all(levelPromises);
        results.forEach(({ id, level }) => {
          levels[id] = level;
        });
      }
      setBeeLevels(levels);
    } catch (error) {
      console.error('Error loading bee levels:', error);
    } finally {
      setLevelsLoading(false);
    }
  }, [getBeeLevel, props.addressBeeTokenIds]);

  useEffect(() => {
    loadBeeLevels();
  }, [loadBeeLevels]);

  // Get unique levels for filter options
  const availableLevels = useMemo(() => {
    const levels = new Set<number>();
    Object.values(beeLevels).forEach((level) => levels.add(level));
    return Array.from(levels).sort((a, b) => a - b);
  }, [beeLevels]);

  // Get level counts for display
  const levelCounts = useMemo(() => {
    const counts: Record<number, number> = {};
    Object.values(beeLevels).forEach((level) => {
      counts[level] = (counts[level] || 0) + 1;
    });
    return counts;
  }, [beeLevels]);

  const filteredBeeOptions = useMemo(() => {
    return beeOptions.filter((option) => {
      // Filter by ID search
      if (searchId && option.value.toString() !== searchId) {
        return false;
      }
      
      // Filter by level
      if (levelFilter !== 'all') {
        const beeLevel = beeLevels[option.value];
        if (beeLevel !== levelFilter) {
          return false;
        }
      }
      
      return true;
    });
  }, [beeOptions, searchId, levelFilter, beeLevels]);

  const loadCombBalances = useCallback(async () => {
    if (!props.address || !contracts.honeycombs) {
      setCombBalance([0, 0, 0, 0, 0]);
      return;
    }

    try {
      const owners = combIds.map(() => props.address as string);
      const balances = await contracts.honeycombs.balanceOfBatch(owners, combIds);
      const normalized = (balances as any[]).map((balance) => {
        if (typeof balance === 'number') return balance;
        if (typeof balance === 'string') return Number(balance);
        if (balance && typeof balance.toString === 'function') {
          return Number(balance.toString());
        }
        return 0;
      });
      setCombBalance(normalized);
    } catch (error) {
      console.error('Error fetching honeycomb balances:', error);
      setCombBalance([0, 0, 0, 0, 0]);
    }
  }, [contracts.honeycombs, props.address]);

  // Fetch comb balance from contract
  useEffect(() => {
    loadCombBalances();
  }, [loadCombBalances]);

  // Reset selections after transaction
  useEffect(() => {
    if (props.address && !props.txPending) {
      loadCombBalances();
      setSelectedCombs([0, 0, 0, 0, 0]);
    }
  }, [props.txPending, props.address, loadCombBalances]);

  const getBorderStyle = (isDesktop: boolean): React.CSSProperties => ({
    borderRadius: '0.375rem',
    marginBottom: '10px',
    padding: '1.2em',
    justifyContent: 'center'
  });

  const rarityToTitle = (rarity: number) => {
    switch (rarity) {
      case 1: return "Uncommon";
      case 2: return "Rare";
      case 3: return "Epic";
      case 4: return "Legendary";
      default: return "Common";
    }
  };

  const getCombImage = (rarity: number) => {
    return combImages[rarity] || combImages[0];
  };

  const updateSelection = (existingCombs: number, combIndex: number, update: number) => {
    let selection = Array.from(selectedCombs);
    let amountPreSelected = selection[combIndex];
    let newSelectionCount = amountPreSelected + update;

    if (newSelectionCount <= existingCombs && newSelectionCount >= 0) {
      selection[combIndex] = newSelectionCount;
      setSelectedCombs(selection);
    }
  };

  const handleBeeSelection = (beeId: number) => {
    setSelectedBee(beeId);
  };

  return (
    <div className="w-full flex flex-col items-center text-center">
      {/* Bee Selection and Leveling Section */}
      <div className="w-full flex flex-row justify-center mb-6">
        <div style={getBorderStyle(props.isDesktop)} className="max-w-2xl w-full bg-black bg-opacity-60 border border-[#55bac3] rounded-md">
          <div className="text-left mb-4">
            <label className="block text-[#c4f6f9] font-bold mb-2 text-sm">Select a Bee</label>
            {beeOptions.length > 0 ? (
              <>
                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-3 mb-3">
                  {/* Search by ID */}
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-400">ID:</label>
                    <input
                      type="text"
                      placeholder="Search by ID"
                      className="w-28 p-2 rounded-md bg-black bg-opacity-60 text-white border border-[#55bac3] focus:border-[#c4f6f9] focus:outline-none text-sm"
                      value={searchId}
                      onChange={(e) => setSearchId(e.target.value.replace(/\D/g, ''))}
                    />
                    {searchId && (
                      <button
                        onClick={() => setSearchId('')}
                        className="text-gray-400 hover:text-white text-sm"
                      >
                        ×
                      </button>
                    )}
                  </div>

                  {/* Level Filter */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <label className="text-xs text-gray-400">Level:</label>
                    <button
                      onClick={() => setLevelFilter('all')}
                      className={`px-2 py-1 rounded-md text-xs transition-colors ${
                        levelFilter === 'all'
                          ? 'bg-[#55bac3] text-black'
                          : 'bg-transparent border border-[#55bac3] text-[#55bac3] hover:bg-[#55bac3]/20'
                      }`}
                    >
                      All
                    </button>
                    {levelsLoading ? (
                      <span className="text-xs text-gray-400 animate-pulse">Loading...</span>
                    ) : (
                      availableLevels.map((level) => (
                        <button
                          key={level}
                          onClick={() => setLevelFilter(level)}
                          className={`px-2 py-1 rounded-md text-xs transition-colors ${
                            levelFilter === level
                              ? 'bg-[#fcd34d] text-black'
                              : 'bg-transparent border border-[#fcd34d] text-[#fcd34d] hover:bg-[#fcd34d]/20'
                          }`}
                        >
                          Lv.{level} ({levelCounts[level] || 0})
                        </button>
                      ))
                    )}
                  </div>
                </div>

                {/* Results count */}
                <div className="text-xs text-gray-400 mb-2">
                  Showing {filteredBeeOptions.length} of {beeOptions.length} bees
                </div>

                <div className="max-h-64 overflow-y-auto border border-[#55bac3] rounded-md">
                  {filteredBeeOptions.map((option) => (
                    <button
                      key={option.key}
                      onClick={() => handleBeeSelection(option.value)}
                      className={`w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-black hover:bg-opacity-80 transition-colors ${
                        selectedBee === option.value ? 'bg-black bg-opacity-80 border-l-4 border-[#ffa2f8]' : 'bg-black bg-opacity-40'
                      }`}
                    >
                      <img src={option.image} alt={option.text} className="w-10 h-10 rounded-md object-cover border border-[#c4f6f9]" />
                      <span className="text-white text-sm">{option.text}</span>
                      <span className="ml-auto text-xs text-[#fcd34d]">
                        Lv.{beeLevels[option.value] ?? '?'}
                      </span>
                    </button>
                  ))}
                  {filteredBeeOptions.length === 0 && (
                    <div className="px-4 py-3 text-gray-400 text-center text-sm">
                      No bees match your filters.
                      <button
                        onClick={() => { setSearchId(''); setLevelFilter('all'); }}
                        className="block mx-auto mt-2 text-[#55bac3] text-xs hover:underline"
                      >
                        Clear filters
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="p-4 text-center bg-black bg-opacity-40 rounded-md border border-gray-700">
                <div className="text-gray-400 text-sm">You don't own any bees to level up.</div>
              </div>
            )}
          </div>
          
          {selectedBee !== 0 && (
            <LevelingBee 
              txPending={props.txPending}
              setTxPending={props.setTxPending}
              selectedCombs={selectedCombs}
              beeId={selectedBee}
              address={props.address}
              isDesktop={props.isDesktop}
            />
          )}
          
          {selectedBee === 0 && (
            <div className="text-[#55bac3] text-sm p-4">
              Select a bee to level it up
            </div>
          )}
        </div>
      </div>

      {/* Honeycomb Cards Section */}
      <div className="w-full p-4 bg-black bg-opacity-60 rounded-md border border-[#55bac3]">
        <div className="text-[#c4f6f9] font-bold mb-4 text-sm">Your Honeycombs</div>
        <div className="flex flex-row flex-wrap justify-center gap-4">
          {combBalance.map((combs, index) => (
            <div key={index} className="bg-black bg-opacity-80 rounded-md shadow-lg overflow-hidden border border-[#55bac3]" style={{width: "170px"}}>
              {/* Header */}
              <div className="bg-black bg-opacity-90 text-[#c4f6f9] text-sm font-bold p-2 text-center border-b border-[#55bac3]">
                {rarityToTitle(index).toUpperCase()}
              </div>
              
              {/* Image */}
              <div className="flex justify-center p-2">
                <img 
                  src={getCombImage(index)} 
                  alt={rarityToTitle(index)}
                  className="w-20 h-20 object-contain"
                  onError={(e) => {
                    e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iOTYiIGhlaWdodD0iOTYiIHZpZXdCb3g9IjAgMCA5NiA5NiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9Ijk2IiBoZWlnaHQ9Ijk2IiBmaWxsPSIjMzc0MTUxIi8+Cjx0ZXh0IHg9IjQ4IiB5PSI1NiIgZmlsbD0iI0ZGRiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZm9udC1mYW1pbHk9Im1vbm9zcGFjZSIgZm9udC1zaXplPSIxNCIgZm9udC13ZWlnaHQ9ImJvbGQiPkNvbWI8L3RleHQ+Cjwvc3ZnPg==';
                  }}
                />
              </div>

              {/* Controls */}
              <div className="p-3">
                {selectedBee > 0 && (
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <span className="text-gray-300 font-bold text-xs">BURN:</span>
                    <button
                      onClick={() => updateSelection(combs, index, -1)}
                      className="w-7 h-7 bg-transparent border-2 border-[#ffa2f8] hover:bg-[#ffa2f8] hover:text-black disabled:border-gray-600 disabled:text-gray-600 disabled:cursor-not-allowed text-[#ffa2f8] rounded-full flex items-center justify-center font-bold text-sm transition-colors"
                      disabled={selectedCombs[index] <= 0}
                    >
                      -
                    </button>
                    <span className="text-white font-bold text-lg min-w-[2ch] text-center">
                      {selectedCombs[index]}
                    </span>
                    <button
                      onClick={() => updateSelection(combs, index, 1)}
                      className="w-7 h-7 bg-transparent border-2 border-[#ffa2f8] hover:bg-[#ffa2f8] hover:text-black disabled:border-gray-600 disabled:text-gray-600 disabled:cursor-not-allowed text-[#ffa2f8] rounded-full flex items-center justify-center font-bold text-sm transition-colors"
                      disabled={selectedCombs[index] >= combs}
                    >
                      +
                    </button>
                  </div>
                )}

                {/* Balance */}
                <div className="text-center">
                  <span className="text-[#fcd34d] text-sm">{combs}</span>
                  <span className="text-gray-400 text-xs ml-1">owned</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default memo(BeeLeveler, (prevProps, nextProps) => {
  // Only re-render if these specific props change
  return (
    prevProps.hiveIds === nextProps.hiveIds &&
    prevProps.txPending === nextProps.txPending &&
    prevProps.isDesktop === nextProps.isDesktop &&
    prevProps.addressBeeTokenIds === nextProps.addressBeeTokenIds &&
    prevProps.address === nextProps.address
  );
});